// AI Agent Service - Orchestrates the conversation with ChatGPT
// Uses secure server-side proxy for OpenAI API

import { executeTool, getToolsForAPI } from './agent-tools';
import { getCompanyInfo } from 'src/api/supabase/company-settings';
import { getAuthHeaders } from 'src/utils/auth-headers';

// API endpoint - uses secure server-side proxy (API key stored on server)
const OPENAI_API_URL = '/api/openai/chat';

// Available models
export type ModelType = 'o4-mini' | 'gpt-4o' | 'advisor' | 'open-claw';

export interface ModelOption {
  id: ModelType;
  name: string;
  description: string;
}

export const AVAILABLE_MODELS: ModelOption[] = [
  { id: 'o4-mini', name: 'ChatGPT o4-mini', description: 'Fast & efficient with business knowledge' },
  { id: 'gpt-4o', name: 'ChatGPT GPT-4o', description: 'Most capable model' },
  { id: 'advisor', name: 'Advisor', description: 'Sales advisor mode' },
  { id: 'open-claw', name: 'Open Claw', description: 'Open Claw AI assistant' },
];

// API key is now handled server-side, but we keep localStorage for user-provided keys
export function getStoredApiKey(): string | null {
  // API key is now handled server-side via proxy
  // Only use localStorage for user-provided keys as fallback
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('relight_openai_api_key');
}

export function setStoredApiKey(key: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('relight_openai_api_key', key);
}

export function clearStoredApiKey(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('relight_openai_api_key');
}

// Server-side API is always configured
export function hasEnvApiKey(): boolean {
  return true; // API key is now on server
}

// Get/Set selected model
export function getSelectedModel(): ModelType {
  if (typeof window === 'undefined') return 'o4-mini';
  return (localStorage.getItem('relight_selected_model') as ModelType) || 'o4-mini';
}

export function setSelectedModel(model: ModelType): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('relight_selected_model', model);
}

export interface Message {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  tool_calls?: Array<{
    id: string;
    type: 'function';
    function: {
      name: string;
      arguments: string;
    };
  }>;
  tool_call_id?: string;
  name?: string;
}

// ============================================
// BUSINESS KNOWLEDGE BASE
// ============================================

const getKnowledgeBase = () => {
  const co = getCompanyInfo();
  const companyName = co.fullCompanyName || 'Relight Exterior Lighting';
  const phone = co.phone || '(425) 465-1290';
  return `
## COMPANY OVERVIEW
${companyName} is an authorized dealer of Jellyfish Lighting. We specialize in high-quality, customizable LED lighting systems that enhance the beauty, safety, and value of properties year-round.

**Mission:** To illuminate homes and businesses with innovative, energy-efficient lighting solutions that create lasting impressions.

**Core Values:**
- Quality Excellence - Premium-grade materials and components
- Customer Focus - Every decision centers on customer satisfaction
- Innovation - Continuously improving products and services
- Integrity - Honest, transparent business practices

**Key Differentiators:**
- Permanent installation (not temporary holiday lights)
- Full RGB color customization with 16 million colors
- Smart home integration (Alexa, Google Home)
- Professional installation by trained technicians
- Lifetime warranty on LED components
- Local, family-owned business
- Financing options available

**Contact:** Phone: ${phone} | Hours: Everyday 7AM-7PM

## PRODUCTS

### Permanent Eave Lighting System (Flagship Product)
- LED Type: Premium SMD 5050 RGB LEDs
- Colors: 16+ million via RGB mixing
- Brightness: Up to 1200 lumens/meter
- Lifespan: 50,000+ hours (~17 years at 8hrs/day)
- Power: 14.4W/meter, 24V DC (safe low voltage)
- IP Rating: IP65 (weather-resistant)
- Operating Temp: -40°F to 140°F

### Track Options
1. Standard Track - Clean, minimal profile
2. Premium Track - Enhanced durability
3. Commercial Track - Heavy-duty for business

### Smart Control
- iOS/Android app
- Schedule lighting on/off
- Custom scenes and colors
- Music sync capability
- Voice control (Alexa, Google Home)

### Warranty
- LED Components: Lifetime warranty
- Control System: 5-year warranty
- Track/Housing: 10-year warranty
- Labor: 2-year warranty

## PRICING & QUOTES

**Typical Pricing:**
- Most residential: $2,500 - $8,000
- 100 feet: $2,500-3,500
- 200 feet: $4,000-6,000
- 300+ feet: $6,500+
- Just front of home: ~$3,000

**What's Included:**
- All materials (LED lights, track, wiring)
- Professional installation
- Control system and app
- Training on system use
- Warranty coverage
- No hidden fees

**Financing:** Yes, flexible options with 0% APR promotions available.

## INSTALLATION

**Timeline:**
- Small Home (<150 ft): 4-6 hours
- Medium Home (150-250 ft): 6-8 hours
- Large Home (250+ ft): 8-10 hours or 2 days

**Process:**
1. Arrival & Setup (30 min)
2. Track Installation (2-4 hrs)
3. Wiring & Connections (1-2 hrs)
4. System Setup (30-60 min)
5. Cleanup & Walkthrough (30 min)

**Customer Prep:**
- Clear access to all areas
- Move vehicles from driveway
- Secure pets
- Identify outdoor outlet/panel

## FREQUENTLY ASKED QUESTIONS

**How long does installation take?** 1-2 days for most homes.

**Will it damage my roof?** No. Track mounts without penetrating roofing materials.

**How do I control the lights?** Smartphone app, voice commands (Alexa/Google), or remote control.

**Do they work year-round?** Yes! Designed for permanent outdoor use in all weather.

**What if an LED stops working?** Individual LEDs can be replaced. Contact us for warranty service.

**Are they waterproof?** Yes, IP65 rated for rain, snow, humidity.

**How much electricity do they use?** Very efficient - 100-200W typical (like a couple light bulbs).

## CUSTOMER SERVICE

**Response Times:**
- Phone: Answered during business hours, voicemail returned within 4 hours
- Email: Within 24 hours
- Emergency: Same-day response

**HEARD Method for Complaints:**
1. Hear - Listen fully without interrupting
2. Empathize - Acknowledge feelings
3. Apologize - Take responsibility
4. Resolve - Offer solution
5. Diagnose - Understand root cause

## TROUBLESHOOTING

**Lights Won't Turn On:**
1. Check control box power (LED should be lit)
2. Verify outlet is working
3. Try the app
4. Check schedules
5. Reset control box (unplug 30 sec)

**App Won't Connect:**
1. Same Wi-Fi network as control box
2. Bluetooth enabled
3. Close/reopen app
4. Toggle Wi-Fi
5. Reinstall app if needed

**Control Box LED Indicators:**
- Solid Green: Normal, connected
- Blinking Green: Connecting to Wi-Fi
- Solid Red: Error - power cycle
- Blinking Red: Firmware updating
- No Light: No power

## BOOKING QUOTE APPOINTMENTS

**Budget Qualification (CRITICAL - Always ask):**
> "Jellyfish Lighting costs around $5,000 to $10,000 for a residential property (sometimes around $3,000 for just the front). Is this something that fits within your budget?"

**Required Before Scheduling:**
1. Budget confirmed ($3K-$10K range)
2. Property address collected
3. Service area confirmed
4. Full contact info (name, phone, email)

**Flow:**
1. Greet and build rapport
2. Understand their needs
3. Ask budget question
4. Get property address
5. Book appointment
6. Set expectations

**What Happens at Quote:**
- Technician arrives (30-45 min visit)
- Walk property together
- Discuss areas to light
- Measurements taken
- Exact quote calculated
- No pressure to decide
- Quote valid 30 days
`;
};

// System prompt for the assistant with knowledge base
const getSystemPrompt = () => {
  const companyName = getCompanyInfo().fullCompanyName || 'Relight Exterior Lighting';
  return `You are ChatGPT, an AI assistant for ${companyName}, an outdoor LED lighting company. You have extensive knowledge about the business and help manage customers and create quotes.

## YOUR KNOWLEDGE BASE
${getKnowledgeBase()}

CRITICAL RULES:
1. When user says "create a quote" - you MUST call create_quote tool. This is separate from creating a customer.
2. Include ALL information the user provides in tool calls.
3. When creating both a customer AND quote, do BOTH steps - don't skip the quote.
4. ACCUMULATE CONTEXT: If a user provides partial info and then adds more in follow-up messages, COMBINE all info before executing.

EXAMPLE 1 - "Create lead John Smith for 150ft, status warm"
Call create_customer with: {"firstName": "John", "lastName": "Smith", "linearFootage": 150, "leadStatus": "warm"}

EXAMPLE 2 - "Create lead Jane Doe and create a quote for 150ft for the front"
Step 1: Call create_customer with {"firstName": "Jane", "lastName": "Doe", "linearFootageFront": 150, "linearFootage": 150}
Step 2: MUST call create_quote with {"customerId": "<id from step 1>", "customerName": "Jane Doe", "linearFootage": 150, "linearFootageFront": 150}

EXAMPLE 3 - "Create a new lead named Zach and create a quote for 150ft for the front. Set the lead status to closed"
Step 1: Call create_customer with {"firstName": "Zach", "leadStatus": "closed", "linearFootageFront": 150, "linearFootage": 150}
Step 2: MUST call create_quote with {"customerId": "<id from step 1>", "customerName": "Zach", "linearFootage": 150, "linearFootageFront": 150}

IMPORTANT: "create a quote" ALWAYS means you must call the create_quote tool. A quote is a pricing document saved to the database - updating customer footage is NOT the same as creating a quote.

FOLLOW-UP INFO FOR QUOTES - CRITICAL:
When user starts with partial info and adds more details in follow-up messages, COMBINE all info:

Example flow:
User: "Create a quote for John Smith"
You: "I'll create a quote for John Smith. How many linear feet should I include? And would you like to specify front, sides, or back of the house?"
User: "150ft for the front and 50ft for each side"
You: [NOW call create_quote with customerName="John Smith", linearFootageFront=150, linearFootageSides=100, linearFootage=250]

Another example:
User: "Create a quote for Sarah Jones 200ft"
You: [Call create_quote with customerName="Sarah Jones", linearFootage=200]
User: "Actually add 100ft for the back too"
You: [Call create_quote AGAIN with customerName="Sarah Jones", linearFootage=300, linearFootageBack=100, linearFootageFront=200]

ALWAYS combine info from the full conversation when the user adds details.

SEND EMAIL - CONVERSATIONAL FLOW:
When user says "I want to send an email" or similar WITHOUT specifying all details, you MUST ask follow-up questions BEFORE calling send_email tool:

1. First ask: "Which customer are you emailing? Please provide their first and last name."
2. Then ask: "What type of email would you like to send?"
   - Quote (sends a quote PDF)
   - Invoice (sends an invoice)
   - Agreement (sends a service agreement)
   - Regular email (plain text message)
3. If they choose "Regular email", ask: "What message would you like to include in the email?"
4. Only AFTER gathering all info, call send_email tool with the complete data.

Example flow:
User: "I want to send an email"
You: "Which customer are you emailing? Please provide their first and last name."
User: "John Smith"
You: "What type of email would you like to send? Options: Quote, Invoice, Agreement, or Regular email"
User: "Quote"
You: [NOW call send_email with recipientName="John Smith", emailType="quote"]

AVAILABLE TOOLS:
- create_customer: Create new customer/lead
- create_quote: Create a quote document (REQUIRED when user says "create a quote"). Uses customerId from create_customer.
- search_customer: Search by name/email/phone
- update_customer: Update a single existing customer (requires customerId)
- get_customer_quotes: Get quotes for customer
- get_recent_leads: List recent leads
- bulk_update_leads: Update multiple leads at once based on status filters. Use for mass updates.
- send_email: Navigate to send email page. REQUIRES recipientName and emailType. Ask user if not provided.
- send_text_message: Navigate to send text page with pre-filled message.
- navigate_to_invoice: Go to invoice creation page.
- navigate_to_agreement: Go to agreements page.
- export_leads: Download all leads as CSV backup.
- customer_analysis: Generate a comprehensive Customer Analysis PDF with all saved data.
- quarterly_analytics: Generate a multi-page Quarterly Analytics PDF report with revenue, profit, sales metrics, and performance data. Use for "quarterly report", "Q1/Q2/Q3/Q4 analytics", etc.

BULK UPDATE EXAMPLE:
User: "Mark all leads besides closed/paid as dead"
Call bulk_update_leads with: {"excludeStatuses": ["closed", "paid"], "newStatus": "dead", "confirmBulkUpdate": true}

User: "Set all warm leads to hot"
Call bulk_update_leads with: {"includeStatuses": ["warm"], "newStatus": "hot", "confirmBulkUpdate": true}

RESPONSE FORMAT:
- Be brief
- Confirm what was saved with specific values
- Include [View Customer](url) links from tool responses`;
};

export interface AgentResponse {
  message: string;
  toolsUsed?: string[];
  error?: string;
}

export async function sendAgentMessage(
  messages: Message[],
  apiKey: string,
  model?: ModelType
): Promise<AgentResponse> {
  return sendOpenAIMessage(messages, apiKey, model);
}

// OpenAI ChatGPT API
async function sendOpenAIMessage(
  messages: Message[],
  apiKey: string,
  model?: ModelType
): Promise<AgentResponse> {
  const selectedModel = model || getSelectedModel();
  const allMessages: Message[] = [
    { role: 'system', content: getSystemPrompt() },
    ...messages
  ];

  try {
    let response = await callOpenAIAPI(allMessages, apiKey, selectedModel);
    let assistantMessage = response.choices[0].message;
    let toolsUsed: string[] = [];

    let iterations = 0;
    const maxIterations = 10;

    while (assistantMessage.tool_calls && iterations < maxIterations) {
      iterations++;

      allMessages.push({
        role: 'assistant',
        content: assistantMessage.content || '',
        tool_calls: assistantMessage.tool_calls
      });

      for (const toolCall of assistantMessage.tool_calls) {
        const toolName = toolCall.function.name;
        toolsUsed.push(toolName);

        let toolParams: Record<string, unknown>;
        try {
          toolParams = JSON.parse(toolCall.function.arguments);
        } catch {
          toolParams = {};
        }

        const toolResult = await executeTool(toolName, toolParams);

        allMessages.push({
          role: 'tool',
          content: JSON.stringify(toolResult),
          tool_call_id: toolCall.id,
          name: toolName
        });
      }

      response = await callOpenAIAPI(allMessages, apiKey, selectedModel);
      assistantMessage = response.choices[0].message;
    }

    return {
      message: assistantMessage.content || 'I completed the task.',
      toolsUsed: toolsUsed.length > 0 ? toolsUsed : undefined
    };
  } catch (error) {
    return {
      message: '',
      error: error instanceof Error ? error.message : 'An error occurred'
    };
  }
}

// Retry with exponential backoff
async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Fetch with timeout
async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs = 30000): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    return response;
  } finally {
    clearTimeout(timeout);
  }
}

// Map our model types to OpenAI model IDs
function getOpenAIModelId(model: ModelType): string {
  switch (model) {
    case 'o4-mini':
      return 'o4-mini';
    case 'advisor':
      return 'gpt-4o'; // Advisor uses gpt-4o but with simpler prompts
    case 'open-claw':
      return 'gpt-4o'; // Open Claw uses gpt-4o
    case 'gpt-4o':
    default:
      return 'gpt-4o';
  }
}

// OpenAI API call via secure server-side proxy
async function callOpenAIAPI(messages: Message[], _apiKey: string, model: ModelType, retryCount = 0): Promise<any> {
  const modelId = getOpenAIModelId(model);

  // Call our secure proxy - API key is on the server
  const response = await fetchWithTimeout(OPENAI_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      model: modelId,
      messages: messages.map(m => {
        const msg: Record<string, unknown> = {
          role: m.role,
          content: m.content
        };
        if (m.tool_calls) msg.tool_calls = m.tool_calls;
        if (m.tool_call_id) msg.tool_call_id = m.tool_call_id;
        if (m.name) msg.name = m.name;
        return msg;
      }),
      tools: getToolsForAPI(),
      tool_choice: 'auto',
      temperature: 0.3,
      max_tokens: 1024
    })
  }, 30000);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error?.message || `OpenAI API error: ${response.status}`;

    if (response.status === 429) {
      throw new Error(errorMessage);
    }

    if (response.status >= 500 && retryCount < 2) {
      await sleep(2000);
      return callOpenAIAPI(messages, _apiKey, model, retryCount + 1);
    }

    throw new Error(errorMessage);
  }

  return response.json();
}
